/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import React, { memo, useState } from 'react';
import classNames from 'classnames';
import { Link, NavLink } from 'react-router-dom';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PERMISSION, PRIVATE_PATH } from '../../constants';
import { AccountManagementIcon, SignOutIcon, GlobalScheduleIcon, AppsIcon, CaretIcon } from '../icons';
import IsAuthorized from '../IsAuthorized/IsAuthorized';
import DailyScheduleIcon from '../icons/DailyScheduleIcon';
import MenuItemIconLabel from './MenuItemIconLabel';
import PastShiftsIcon from '../icons/PastShiftsIcon';
import SettingsIcon from '../icons/SettingsIcon';
import ProfessionalsIcon from '../icons/ProfessionalsIcon';
import UserAvatar from '../Avatar/UserAvatar';
import menuIcon from '../../assets/images/menu.svg';

import './AppHeaderMobile.scss';

const AppHeaderMobile = ({
	// User and facility data
	showFacilitiesDropdown,
	userData,
	activeFacility,
	selectedFacilityLabel,

	// Feature flags and access control
	isPastShiftsAllowed,
	hasProfessionalsFeature,
	hasSettingsAccess,

	// UI state
	showMobileMenu,
	setShowMobileMenu,
	menuOpen,
	setMenuOpen,
	showPastShiftsCircle,

	// Refs
	typeaheadRef,
	dropdownMenuRef,
	containerRef,
	headerRef,
	toggleMenuRef,
	facilitiesSelectorRef,

	// Custom components
	CustomToggle,
	CustomMenu,

	// Event handlers
	handleNavBarNotOnMobileClick,
	handleAccountManagementClick,
	onLogOut,

	// Miscellaneous
	shiftsToReview,
	professionalsPath,
	isOnGlobalOverview
}) => {
	const [globalMenuExpanded, setGlobalMenuExpanded] = useState(false);

	const toggleGlobalMenu = () => setGlobalMenuExpanded(!globalMenuExpanded);

	return (
		<header className="app-header-mobile" ref={headerRef}>
			<Link to="/" className="logo" />
			<div className={classNames('facility-and-menu', { 'open slide': showMobileMenu })} ref={containerRef}>
				<div className="top-content">
					<div className="user">
						{(userData.name || userData.email) && (
							<>
								<div className="user-section__avatar">
									<UserAvatar initials={userData.initials} />
								</div>
								<div className="user-data">
									<div className="user-name">{userData.name}</div>
									<div className="user-email">{userData.email}</div>
								</div>
							</>
						)}
					</div>

					<div
						className="core-menu-item"
						onClick={handleAccountManagementClick}
						role="button"
						aria-hidden="true"
						tabIndex={0}
					>
						<MenuItemIconLabel icon={<AccountManagementIcon color="#FFF" />} label="Manage Account" />
					</div>

					<div className="core-menu-item" onClick={onLogOut} role="button" aria-hidden="true" tabIndex={0}>
						<MenuItemIconLabel icon={<SignOutIcon color="#FFF" />} label="Sign Out" />
					</div>
					<div className="divider-line" />

					<div className="facility-label-dropdown" ref={facilitiesSelectorRef}>
						{showFacilitiesDropdown ? (
							<Dropdown
								onClick={() => {
									setMenuOpen((prev) => ({ user: false, apps: false, facilities: !prev.facilities }));
									if (!menuOpen.facilities) {
										setTimeout(() => {
											typeaheadRef.current.focus();
											typeaheadRef.current.clear();
										}, 10);
									}
								}}
								show={menuOpen.facilities}
							>
								<Dropdown.Toggle id="facility-enabled-search-dropdown" as={CustomToggle}>
									{selectedFacilityLabel}
								</Dropdown.Toggle>
								<Dropdown.Menu ref={dropdownMenuRef} as={CustomMenu} />
							</Dropdown>
						) : (
							<div>{activeFacility?.label}</div>
						)}
					</div>
				</div>

				<div className="content-menu">
					{!isOnGlobalOverview && (
						<>
							<NavLink
								to={PRIVATE_PATH.SHIFTS}
								className="content-menu-item"
								onClick={() => setShowMobileMenu(false)}
								activeClassName="active"
							>
								<MenuItemIconLabel
									icon={<DailyScheduleIcon color="black" />}
									label="Daily&nbsp;Schedule"
									labelClass="mobile-black-text"
								/>
							</NavLink>

							{isPastShiftsAllowed && (
								<NavLink
									to={PRIVATE_PATH.PAST_SHIFTS}
									className="content-menu-item"
									onClick={handleNavBarNotOnMobileClick}
									activeClassName="active"
								>
									<MenuItemIconLabel
										icon={<PastShiftsIcon color="black" />}
										label="Past&nbsp;shifts"
										showRedCircle={showPastShiftsCircle}
										redNumber={shiftsToReview}
										labelClass="mobile-black-text"
									/>
								</NavLink>
							)}

							{hasProfessionalsFeature && (
								<IsAuthorized
									exactPermission={[PERMISSION.PROFESSIONALS, PERMISSION.INTERNAL_STAFF]}
									readOnlyRender={
										<OverlayTrigger
											trigger={['hover', 'focus']}
											key="professionals-tooltip"
											placement="bottom"
											overlay={
												<Tooltip className="professionals-menu-item-tooltip" placement="bottom" show>
													You do not have permission to perform this action. Please contact your Customer Success Rep
													for details.
												</Tooltip>
											}
										>
											<div className="content-menu-item">
												<MenuItemIconLabel
													icon={<ProfessionalsIcon color="black" />}
													label="Professionals"
													labelClass="mobile-black-text"
												/>
											</div>
										</OverlayTrigger>
									}
								>
									<NavLink
										to={professionalsPath}
										className="content-menu-item"
										onClick={() => setShowMobileMenu(false)}
										activeClassName="active"
									>
										<MenuItemIconLabel
											icon={<ProfessionalsIcon color="black" />}
											label="Professionals"
											labelClass="mobile-black-text"
										/>
									</NavLink>
								</IsAuthorized>
							)}
							{hasSettingsAccess && (
								<NavLink
									to={{ pathname: PRIVATE_PATH.SETTINGS }}
									className="content-menu-item"
									onClick={handleNavBarNotOnMobileClick}
									activeClassName="active"
								>
									<MenuItemIconLabel
										icon={<SettingsIcon color="black" width={26} height={26} />}
										label="Settings"
										labelClass="mobile-black-text"
									/>
								</NavLink>
							)}
						</>
					)}
					{(userData.flexGlobalOverviewEnabled || userData.onDemandGlobalOverviewEnabled) && (
						<>
							<div
								className={classNames('content-menu-item', 'global-menu-expandable', {
									active: !globalMenuExpanded && isOnGlobalOverview
								})}
								onClick={toggleGlobalMenu}
							>
								<AppsIcon width={20} height={20} color="black" />
								<span className="main-menu-label mobile-black-text">Global Menu</span>
								<CaretIcon
									pointTo={globalMenuExpanded ? 'up' : 'down'}
									color="black"
									width={18}
									className="toggle__caret"
								/>
							</div>
							{globalMenuExpanded && [
								<NavLink
									to={PRIVATE_PATH.GLOBAL_OVERVIEW}
									className="content-menu-item"
									onClick={() => setShowMobileMenu(false)}
									activeClassName="active"
									key="global-schedule"
								>
									<MenuItemIconLabel
										icon={<GlobalScheduleIcon color="black" />}
										label="Global &nbsp;Schedule"
										labelClass="mobile-black-text"
									/>
								</NavLink>,
								<NavLink
									to={PRIVATE_PATH.SHIFTS}
									className="content-menu-item"
									onClick={() => setShowMobileMenu(false)}
									activeClassName=""
									key="facilitiy-view"
								>
									<MenuItemIconLabel
										icon={<DailyScheduleIcon color="black" />}
										label="Facility View"
										labelClass="mobile-black-text"
									/>
								</NavLink>
							]}
						</>
					)}
				</div>
			</div>
			<div
				ref={toggleMenuRef}
				className="menu-hamburger cursor-pointer"
				onClick={() => {
					setShowMobileMenu(!showMobileMenu);
				}}
				aria-hidden
			>
				<img src={menuIcon} alt="" className="header-menu-icon" />
			</div>
		</header>
	);
};

export default memo(AppHeaderMobile);
